<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import SalesTransactionsTableRow from './SalesTransactionsTableRow.vue';
import SalesTransactionsTableToolbar from './SalesTransactionsTableToolbar.vue';

const props = defineProps({
  activeList: { type: String, required: true },
  busyActions: { type: Object, default: () => ({}) },
  error: { type: String, default: '' },
  loading: { type: Boolean, default: false },
  rows: { type: Array, default: () => [] },
  showManager: { type: Boolean, default: false },
  page: { type: Number, default: 1 },
  totalPages: { type: Number, default: 1 },
  totalRows: { type: Number, default: 0 },
});

const emit = defineEmits([
  'archive',
  'changePage',
  'details',
  'deliver',
  'finalize',
  'register',
  'restore',
  'review',
  'signatures',
]);
const { t } = useI18n();

const isReviewList = computed(() => props.activeList === 'revisoes');
const isDeliveredList = computed(() =>
  ['entregues', 'finalizados'].includes(props.activeList)
);
const baseColumnCount = computed(() => {
  if (isReviewList.value) return 6;
  if (isDeliveredList.value) return 8;
  return 4;
});
const columnCount = computed(() => {
  return baseColumnCount.value + Number(props.showManager);
});
</script>

<template>
  <section
    data-testid="sales-transactions-table"
    class="min-w-0 overflow-hidden rounded-[12px] border-2 border-lais-border bg-white"
  >
    <SalesTransactionsTableToolbar
      :active-list="activeList"
      :page="page"
      :total-pages="totalPages"
      :total-rows="totalRows"
      :visible-rows="rows.length"
      @change-page="emit('changePage', $event)"
    >
      <template v-if="$slots['toolbar-start']" #start>
        <slot name="toolbar-start" />
      </template>
    </SalesTransactionsTableToolbar>

    <div v-if="$slots['active-filters']" class="px-[23px] pb-2">
      <slot name="active-filters" />
    </div>

    <p
      v-if="error"
      class="m-4 rounded-lais border border-red-200 bg-red-50 p-3 text-sm text-red-700"
    >
      {{ error }}
    </p>

    <div class="overflow-x-auto">
      <table class="w-full min-w-[1080px] text-left text-sm">
        <thead
          class="border-b-2 border-[#FFB36B] bg-white text-sm font-normal text-lais-text"
        >
          <tr>
            <th v-if="showManager" class="px-4 py-[18px]">
              {{ t('SALES.TABLE.MANAGER') }}
            </th>
            <th class="px-4 py-[18px]">{{ t('SALES.TABLE.ADDRESS') }}</th>
            <template v-if="isReviewList">
              <th class="px-4 py-[18px]">
                {{ t('SALES.TABLE.SIGNATURE_DATE') }}
              </th>
              <th class="px-4 py-[18px]">
                {{ t('SALES.TABLE.REQUEST_DATE') }}
              </th>
              <th class="px-4 py-[18px]">
                {{ t('SALES.TABLE.ELAPSED_DAYS') }}
              </th>
              <th class="px-4 py-[18px]">{{ t('SALES.TABLE.CORRECTIONS') }}</th>
            </template>
            <template v-else-if="isDeliveredList">
              <th class="px-4 py-[18px]">
                {{ t('SALES.TABLE.SIGNATURE_DATE') }}
              </th>
              <th class="px-4 py-[18px]">
                {{ t('SALES.TABLE.WRITING_DEADLINE') }}
              </th>
              <th class="px-4 py-[18px]">{{ t('SALES.TABLE.PAYMENT') }}</th>
              <th class="px-4 py-[18px]">{{ t('SALES.TABLE.POST_SALE') }}</th>
              <th class="px-4 py-[18px]">
                {{ t('SALES.TABLE.STATUS_DEADLINE') }}
              </th>
              <th class="px-4 py-[18px]">{{ t('SALES.TABLE.RECEIPT') }}</th>
            </template>
            <template v-else>
              <th class="px-4 py-[18px]">{{ t('SALES.TABLE.PROGRESS') }}</th>
              <th class="px-4 py-[18px]">{{ t('SALES.TABLE.STATUS') }}</th>
            </template>
            <th class="px-4 py-[18px]">
              <span class="sr-only">{{ t('SALES.ARIA.ACTIONS') }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td
              :colspan="columnCount"
              class="p-10 text-center text-lais-text-muted"
            >
              {{ t('SALES.COMMON.LOADING') }}
            </td>
          </tr>
          <template v-else>
            <SalesTransactionsTableRow
              v-for="transaction in rows"
              :key="transaction.id"
              :active-list="activeList"
              :busy-action="busyActions[transaction.id]"
              :show-manager="showManager"
              :transaction="transaction"
              @archive="emit('archive', $event)"
              @details="emit('details', $event)"
              @deliver="emit('deliver', $event)"
              @finalize="emit('finalize', $event)"
              @register="emit('register', $event)"
              @restore="emit('restore', $event)"
              @review="emit('review', $event)"
              @signatures="emit('signatures', $event)"
            />
            <tr v-if="!rows.length">
              <td
                :colspan="columnCount"
                class="px-4 py-5 text-left text-lais-text"
              >
                {{ t('SALES.COMMON.NO_DATA') }}
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
  </section>
</template>
